/**
 * app.js
 *
 * This is the entry file for the application, only setup and boilerplate
 * code.
 */

import React from 'react';
import { createRoot } from 'react-dom/client';
import { Provider as StoreProvider } from 'react-redux';

import { LicenseInfo as MuiLicenseInfo } from '@mui/x-license';

import 'sanitize.css/sanitize.css';
import 'react-tippy/dist/tippy.css';

import 'core-js/stable';
import 'regenerator-runtime/runtime';

import * as am4core from '@amcharts/amcharts4/core';
import data from '@emoji-mart/data';
import { init as initEmojiMart } from 'emoji-mart';

// make sure to initialize react core before importing any of our components (including other react components)
import 'utils/core';
import 'utils/interfaces/tracker';
import 'utils/interfaces/assert';
import 'utils/interfaces/crahReporter';
import 'utils/interfaces/httpClient';
import 'utils/interfaces/bump';

import { loggerConfigurator, getLogger, tracker } from 'app-components/utils/core';
import { useRehydratedStore } from 'app-components/utils/persist';
import { getCommonConfig } from 'app-components/utils/core';
import { HTML5Backend } from 'react-dnd-html5-backend';

import 'utils/tabAuthExchange'; // exchange auth session with other browser tabs
import 'utils/wdyr'; // instruments/wraps react code, so should always be the first import before render started

import AppRouter from 'containers/AppRouter';
import { appSetOnlineStatus } from 'data/app/actions';

import consoleMirror from 'utils/consoleMirror';
import { captureNetworkLogs } from 'utils/networkLogs';
import ErrorBoundary from 'components/ErrorBoundary';
import { measureTTI } from 'utils/tti';

// Load the favicon and the .htaccess file
import '!file-loader?name=favicon.[ext]!./images/favicon-simplifi.ico';
import '!file-loader?name=apple-touch-icon.[ext]!./images/apple-touch-icon-simplifi.png';
import '!file-loader?name=.well-known/[name].[ext]!./files/assetlinks.json';
// Apple association file for universal linking to the iOS app.
import '!file-loader?name=.well-known/[name]!./files/apple-app-site-association';
import '!file-loader?name=[name].[ext]!./assets/logos/simplifi-logo-text-dark.svg';
import '!file-loader?name=[name].[ext]!./assets/logos/Quicken_Logo_animated.gif';
import '!file-loader?name=[name].[ext]!./assets/logos/Quicken_Logo_animated_dark.gif';
import '!file-loader?name=[name].[ext]!./assets/logos/Quicken_Logo_static.gif';
import 'file-loader?name=[name].[ext]!./.htaccess';
import '!file-loader?name=[name].[ext]!./files/barchart.html';

///////////////////////////////////////////////////////////////////////////////////////////////////////////////
// Load for stripe test env apple pay
import '!file-loader?name=.well-known/[name]!./files/apple-developer-merchantid-domain-association';
/////////

import { createPersistConfig } from './createPersistConfig';
import { createStore, persistedAuthSession } from './store';
import CombinedStyleProviders from './CombinedStyleProviders';
import PersistLoading from './components/PersistLoading';
import { DndProvider } from 'react-dnd';

// TODO: Import selector for `syncHistoryWithStore`
// import { makeSelectLocationState } from 'containers/App/selectors';

MuiLicenseInfo.setLicenseKey(getCommonConfig().mui_x_pro_license);

am4core.options.commercialLicense = true;

const qcsId = persistedAuthSession?.qcsId;
const datasetId = persistedAuthSession?.datasetId;
tracker.identify(qcsId); // identify tracker ASAP

consoleMirror.attach();

///////////////////////////////////////////////////////////////////////////////////////////////////////////////
loggerConfigurator.setFeatureFlagLoggingLevel(0); // 0 = DEBUG for dev builds
/////////

const log = getLogger('app.js');
log.info(
  '' +
    `\napp version: ${process.env.APP_VERSION} (${process.env.BUILD_NUMBER})` +
    `\nenvironment: ${process.env.NODE_ENV} (${process.env.SERVICE_ENV})`,
);

captureNetworkLogs(XMLHttpRequest.prototype);

tracker.track(tracker.events.init);

initEmojiMart({ data });

function nodeListPolyfill() {
  if (typeof NodeList.prototype.forEach === 'function') return false;
  NodeList.prototype.forEach = Array.prototype.forEach;
  return true;
}

nodeListPolyfill();

// ===============================================================================================
// React APP Render
// ===============================================================================================
const isLoginMode = window !== window.top; // app is inside auth widget iframe
const cannotRehydrate = !qcsId || !datasetId;
const container = document.getElementById('app');
const root = createRoot(container); // createRoot(container!) if you use TypeScript
function Root() {
  // If the user is not logged in we can't rehydrate. This override will skip rehydration and use our initial state object to initialize the store instead
  const initialStateOverride = isLoginMode || cannotRehydrate ? {} : undefined;
  const store = useRehydratedStore(
    (initialState) => createStore(true, initialState),
    () => createPersistConfig(qcsId, datasetId),
    initialStateOverride,
  );

  const handleNetworkChange = React.useCallback(
    (event) => {
      if (!store) {
        return;
      }

      if (navigator.onLine) {
        const trigger = event ? 'event' : 'navigator';
        log.info(`Application is online (${trigger})`, navigator.onLine, event?.type);
        store.dispatch(appSetOnlineStatus(true));
        tracker.track(tracker.events.connection, {
          state: 'online',
          trigger,
          navigator: navigator.onLine,
          event: event?.type,
        });
      } else {
        const trigger = event ? 'event' : 'navigator';
        log.info(`Application is offline (${trigger})`, navigator.onLine, event?.type);
        store.dispatch(appSetOnlineStatus(false));
        tracker.track(tracker.events.connection, {
          state: 'offline',
          trigger,
          navigator: navigator.onLine,
          event: event?.type,
        });
      }
    },
    [store],
  );

  React.useEffect(() => {
    if (!store) {
      return;
    }

    window.addEventListener('online', handleNetworkChange);
    window.addEventListener('offline', handleNetworkChange);
    handleNetworkChange();
  }, [store, handleNetworkChange]);

  if (!store) {
    return <PersistLoading />;
  }

  return (
    <StoreProvider store={store}>
      <CombinedStyleProviders>
        <ErrorBoundary>
          <DndProvider backend={HTML5Backend} context={window}>
            <AppRouter />
          </DndProvider>
        </ErrorBoundary>
      </CombinedStyleProviders>
    </StoreProvider>
  );
}

if (module.hot) {
  // Hot reloadable React components and translation json files
  // modules.hot.accept does not accept dynamic dependencies,
  // have to be constants at compile-time
  module.hot.accept(['containers/AppRouter'], () => {
    // root.unmount();
    root.render(<Root />);
  });
}

root.render(<Root />);

// eslint-disable-next-line no-constant-condition
// if (false && process.env.NODE_ENV !== 'production') {
//   const { whyDidYouUpdate } = require('why-did-you-update'); // eslint-disable-line
//   whyDidYouUpdate(React);
// }

window.onclick = () => {
  tracker.trackActive();
  measureTTI();
};

// var installPromptEvent;
// window.addEventListener('beforeinstallprompt', function(event) {
//   event.preventDefault();
//   event.prompt();
//   installPromptEvent = event;
//   console.log('Got BeforeInstallPrompt event');
// });

document.addEventListener('securitypolicyviolation', (event) => {
  tracker.track(tracker.events.securityPolicyViolation, {
    type: event.type,
    status_code: event.statusCode,
    document_uri: event.documentURI,
    blocked_uri: event.blockedURI,
    violated_directive: event.violatedDirective,
    original_policy: event.originalPolicy,
  });
});
